import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Chip from '@mui/material/Chip'
import { blueGrey } from '@mui/material/colors'
import PropTypes from 'prop-types'

import { getCountryName, getStateName } from '@/common/helpers/countryUtilities'
import { fractionToPercentage } from '@/common/helpers/formatUtilities'

/**
 * Displays a tax rate as a styled chip with location and percentage info
 * @param {Object} props
 * @param {Object} props.quoteTaxRate - Tax rate configuration object
 * @param {string} [props.quoteTaxRate.country] - Country code for the tax rate
 * @param {string} props.quoteTaxRate.displayName - Display name for the tax rate
 * @param {number} props.quoteTaxRate.percentage - Tax rate as a decimal fraction (e.g., 0.1 for 10%)
 * @param {string} [props.quoteTaxRate.state] - State/region code for the tax rate
 * @returns {JSX.Element}
 */
const QuoteTaxRateChip = ({ quoteTaxRate }) => {
    const { t } = useTranslation()

    const label = useMemo(() => {
        const { country, displayName, percentage, state } = quoteTaxRate
        const stateLabel = state ? `${getStateName(country, state)} - ` : ''
        const countryLabel = country ? `${getCountryName(country)} - ` : ''
        const percentageLabel = fractionToPercentage(percentage)
        return `${displayName} - ${stateLabel}${countryLabel} (${percentageLabel}%)`
    }, [quoteTaxRate])

    return (
        <Chip
            label={label}
            size="small"
            sx={{
                textTransform: 'uppercase',
                fontSize: '11px',
                backgroundColor: blueGrey[200],
                color: blueGrey[900],
                userSelect: 'none',
            }}
            title={`${t('Tax rate')}: ${label}`}
        />
    )
}

QuoteTaxRateChip.propTypes = {
    quoteTaxRate: PropTypes.shape({
        displayName: PropTypes.string.isRequired,
        percentage: PropTypes.number.isRequired,
        country: PropTypes.string,
        state: PropTypes.string,
    }).isRequired,
}

export default QuoteTaxRateChip
